import { useState } from 'react'
import { Icon } from '@iconify/react'
import { MONTHLY_MEDALS, type MonthlyMedalId } from '@/lib/monthly-medals'
import {
  EMPTY_ACHIEVEMENT_REWARDS,
  type AchievementMessage,
  type CampaignGamification,
} from '@/lib/campaign-gamification'
import { AchievementRewardsEditor } from './AchievementRewardsEditor'
import styles from './GamificacaoTab.module.css'

export function MonthlyMedalsAdmin({
  messages,
  saving,
  onChange,
}: {
  messages: CampaignGamification['achievements']
  saving: boolean
  onChange: (id: MonthlyMedalId, message: AchievementMessage) => void
}) {
  const [expanded, setExpanded] = useState<string[]>([])
  return (
    <section className={styles.categoryContainer} aria-labelledby="admin-medals-title">
      <header className={styles.categoryHeader}>
        <h3 id="admin-medals-title">Medalhas</h3>
        <span>12 medalhas</span>
        <p>
          Duas consultas veterinárias pagas no mesmo mês e campanha conquistam a medalha.
          Os extras são entregues uma vez por mês, ano e comprador.
        </p>
      </header>
      <div className={styles.categoryPanels}>
        {MONTHLY_MEDALS.map((medal, index) => {
          const item = messages[medal.id] ?? {
            enabled: false,
            title: `Medalha de ${medal.month}`,
            text: null,
            video_url: null,
            rewards: EMPTY_ACHIEVEMENT_REWARDS,
          }
          const open = expanded.includes(medal.id)
          const update = (patch: Partial<AchievementMessage>) =>
            onChange(medal.id, { ...item, ...patch })
          return (
            <section key={medal.id} className={styles.panel}>
              <button
                type="button"
                className={styles.expandButton}
                aria-expanded={open}
                aria-controls={`${medal.id}-panel`}
                onClick={() =>
                  setExpanded((current) =>
                    open
                      ? current.filter((id) => id !== medal.id)
                      : [...current, medal.id]
                  )
                }
              >
                <img src={medal.image} alt="" />
                <span>
                  <small>MEDALHA {String(index + 1).padStart(2, '0')}</small>
                  <strong>{medal.month}</strong>
                  <span>2 consultas pagas no mês</span>
                </span>
                <Icon icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} />
              </button>
              {open && (
                <div id={`${medal.id}-panel`} className={styles.card}>
                  <div className={styles.badgePane}>
                    <img src={medal.image} alt="" />
                    <h3>{medal.month}</h3>
                    <p>Duas compras pela clínica do mascote</p>
                  </div>
                  <div className={styles.fields}>
                    <label>
                      <input
                        type="checkbox"
                        checked={item.enabled === true}
                        disabled={saving}
                        onChange={(event) => update({ enabled: event.target.checked })}
                      />{' '}
                      Ativar mensagem e extras desta medalha
                    </label>
                    <label htmlFor={`${medal.id}-title`}>Título</label>
                    <input
                      id={`${medal.id}-title`}
                      value={item.title}
                      maxLength={120}
                      disabled={saving}
                      onChange={(event) => update({ title: event.target.value })}
                    />
                    <label htmlFor={`${medal.id}-text`}>Mensagem ao conquistar</label>
                    <textarea
                      id={`${medal.id}-text`}
                      value={item.text ?? ''}
                      maxLength={4000}
                      disabled={saving}
                      onChange={(event) => update({ text: event.target.value })}
                    />
                    <label htmlFor={`${medal.id}-locked`}>
                      Mensagem antes de conquistar
                    </label>
                    <textarea
                      id={`${medal.id}-locked`}
                      value={item.locked_reason ?? ''}
                      maxLength={2000}
                      disabled={saving}
                      onChange={(event) => update({ locked_reason: event.target.value })}
                    />
                    <label htmlFor={`${medal.id}-video`}>
                      Vídeo do YouTube (opcional)
                    </label>
                    <input
                      id={`${medal.id}-video`}
                      type="url"
                      value={item.video_url ?? ''}
                      maxLength={500}
                      disabled={saving}
                      onChange={(event) => update({ video_url: event.target.value })}
                    />
                    <AchievementRewardsEditor
                      annual
                      id={medal.id}
                      value={item.rewards ?? EMPTY_ACHIEVEMENT_REWARDS}
                      disabled={saving || !item.enabled}
                      onChange={(rewards) => update({ rewards })}
                    />
                  </div>
                </div>
              )}
            </section>
          )
        })}
      </div>
    </section>
  )
}
